"use client";

import React, { useState, useEffect } from "react";
import { Download, Smartphone, Monitor, CheckCircle2, Share, PlusSquare, X, Sparkles } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function CrmPwaInstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isMacSafari, setIsMacSafari] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // 1. Check if running in standalone PWA mode
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true ||
      (document.referrer && document.referrer.includes("android-app://"));

    if (standalone) {
      setIsStandalone(true);
      setInstalled(true);
      return;
    }

    // 2. Check localStorage
    if (localStorage.getItem("johnson_crm_pwa_installed") === "true") {
      setInstalled(true);
    }

    // 3. Detect platform
    const ua = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    setIsIos(isIosDevice);
    setIsMacSafari(!isIosDevice && isSafari && /macintosh|mac os x/.test(ua));

    // 4. Capture beforeinstallprompt for Chrome, Edge, Android, etc.
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setInstalled(true);
      setIsStandalone(true);
      setDeferredPrompt(null);
      localStorage.setItem("johnson_crm_pwa_installed", "true");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setInstalled(true);
        setIsStandalone(true);
        localStorage.setItem("johnson_crm_pwa_installed", "true");
      }
      setDeferredPrompt(null);
      return;
    }

    // Otherwise show guidance modal (iOS Safari, macOS Safari, or manual prompt)
    setShowModal(true);
  };

  if (isStandalone) {
    return (
      <div 
        className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold"
        title="Running in Standalone CRM Enterprise PWA Mode"
      >
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[11px] font-mono font-bold tracking-tight">PWA Native Active</span>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleInstallClick}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-cyan-500/15 via-blue-500/15 to-indigo-500/15 hover:from-cyan-500/25 hover:to-blue-500/25 border border-cyan-500/30 text-cyan-400 hover:text-cyan-300 text-xs font-bold transition-all shadow-xs active:scale-95 cursor-pointer"
        title="Install Johnson Softwares CRM as a standalone desktop or mobile application (Tier 3 Ready)"
      >
        <Download className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
        <span className="hidden sm:inline">Install CRM App</span>
        <span className="sm:hidden">Install</span>
        <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-cyan-400/20 text-cyan-300 font-mono">
          PWA
        </span>
      </button>

      {/* Instructional Modal for Safari / Manual Install */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-800 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                  {isIos ? (
                    <Smartphone className="w-5 h-5" />
                  ) : (
                    <Monitor className="w-5 h-5" />
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                    Install Johnson CRM App
                    <span className="text-[10px] font-extrabold px-1.5 py-0.2 rounded bg-cyan-500/20 text-cyan-400">
                      Tier 3 PWA
                    </span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    Standalone Desktop &amp; Mobile Workspace
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 text-xs text-slate-300 space-y-2.5">
              <div className="flex items-center gap-2 text-emerald-400 font-semibold text-xs mb-1">
                <Sparkles className="w-4 h-4" />
                <span>Enterprise Features Activated:</span>
              </div>
              <p className="text-[11px] text-slate-400">
                • 1-Click Launch from Mac Dock, Windows Taskbar, or Mobile Home Screen
              </p>
              <p className="text-[11px] text-slate-400">
                • Offline Shell Cache &amp; Resilient Network Sync
              </p>
              <p className="text-[11px] text-slate-400">
                • Full-screen native interface with zero browser address bar clutter
              </p>
            </div>

            {isIos ? (
              <div className="space-y-2 text-xs text-slate-300">
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-950/50 border border-slate-800">
                  <Share className="w-4 h-4 text-blue-400 shrink-0" />
                  <span>1. Tap the <strong>Share</strong> icon at the bottom of Safari</span>
                </div>
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-950/50 border border-slate-800">
                  <PlusSquare className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span>2. Select <strong>Add to Home Screen</strong></span>
                </div>
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-950/50 border border-slate-800">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>3. Tap <strong>Add</strong> to launch the native app</span>
                </div>
              </div>
            ) : isMacSafari ? (
              <div className="space-y-2 text-xs text-slate-300">
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-950/50 border border-slate-800">
                  <Share className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span>1. In Safari top menu, click <strong>File</strong> or the <strong>Share</strong> button</span>
                </div>
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-950/50 border border-slate-800">
                  <PlusSquare className="w-4 h-4 text-blue-400 shrink-0" />
                  <span>2. Select <strong>Add to Dock...</strong></span>
                </div>
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-950/50 border border-slate-800">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>3. Click <strong>Add</strong> to create the standalone macOS app!</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-xs text-slate-300">
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-950/50 border border-slate-800">
                  <Download className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span>Click the <strong>Install icon</strong> in your browser&apos;s address bar, or select <strong>Install Johnson Softwares CRM</strong> from the browser settings menu.</span>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowModal(false)}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-bold text-xs transition-all cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
